import { OneHotExclusionConfigT } from "../flatbuffers/generated/one-hot-exclusion/one-hot-exclusion-config";
import { MessageT } from "../flatbuffers/generated/ln2/message";
import { msgTypeLabel, resolveValue } from "../flatbuffers/decoders/applyDeltaToState";

/**
 * Key for exclusion map lookups: "category:enumValue" string.
 * Using string keys for O(1) Map lookups matching the existing
 * EXCLUSIVE_GROUPS pattern in applyDeltaToState.
 */
export type ExclusionKey = string;

/**
 * Per-key exclusion record carrying the symmetric peer list plus the
 * two new per-group flags that the frontend now honours when applying
 * eviction.  Encapsulating this in a record (instead of three parallel
 * maps) keeps lookups O(1) and the call sites readable.
 *
 * - peers: symmetric list of other ExclusionKeys that share the group.
 * - evictOnlyWhenActive: when true (the schema default), an incoming
 *   message with active=false must NOT clear peers — Up=false and
 *   Down=false can coexist.
 * - emitDeactivations: server-side hint, mirrored here so the frontend
 *   editor can round-trip the value.  Not consumed by the local
 *   apply-delta path (which has no concept of downstream emission).
 */
export interface ExclusionRule {
  peers: ExclusionKey[];
  evictOnlyWhenActive: boolean;
  emitDeactivations: boolean;
}

/**
 * Default rule applied when no config is present.  Mirrors the
 * legacy hardcoded `last_wins` behavior: any incoming evicts peers
 * (matching the old applyDeltaToState behaviour where only `active`
 * incomings triggered eviction via `&& active`).
 */
export const DEFAULT_LEGACY_EXCLUSION_RULE: Omit<ExclusionRule, "peers"> = {
  evictOnlyWhenActive: true,
  emitDeactivations: false,
};

/**
 * Extract the bitmask key suffix from a MessageT.
 * Returns "CategoryName:EnumName" (e.g., "Usage:FrontDoorOpened") or null if not extractable.
 */
function exclusionKeyFromMessageT(msg: MessageT): ExclusionKey | null {
  const typeLabel = msgTypeLabel(msg.contentType, msg.content?.messageType ?? 0);
  const value = resolveValue({ contentType: msg.contentType, content: msg.content });
  if (!typeLabel || !value) return null;
  return `${typeLabel}:${value}`;
}

/**
 * Build a directed exclusion Map from the symmetric FlatBuffer config.
 * For each group entry with members [A, B, C], produces:
 *   "cat:A" → { peers: ["cat:B", "cat:C"], evictOnlyWhenActive, emitDeactivations }
 *   "cat:B" → { peers: ["cat:A", "cat:C"], ... }
 *   "cat:C" → { peers: ["cat:A", "cat:B"], ... }
 *
 * The key format matches the bitmask key format used in applyDeltaToState:
 * "CategoryName:EnumName" (e.g., "Usage:FrontDoorOpened").
 *
 * When the same key appears in multiple groups, flags are ANDed
 * (strictest policy wins) — matching the VTS/LiftCloud merge semantics.
 *
 * @param config - The OneHotExclusionConfigT from the adapter CDO
 * @returns Map from message key to ExclusionRule
 */
export function buildExclusionMap(
  config: OneHotExclusionConfigT | null | undefined,
): Map<ExclusionKey, ExclusionRule> {
  const map = new Map<ExclusionKey, ExclusionRule>();

  if (!config) return map;

  config.groups
    .filter((group) => !group.categoryWide)
    .forEach((group) => {
      group.entries.forEach((entry) => {
        const keys = entry.members
          .map((m) => exclusionKeyFromMessageT(m))
          .filter((k): k is ExclusionKey => k !== null);

        keys.forEach((memberKey, i) => {
          const excluded = keys.filter((_, j) => j !== i);

          const existing = map.get(memberKey);
          if (existing) {
            excluded.forEach((ex) => {
              if (!existing.peers.includes(ex)) {
                existing.peers.push(ex);
              }
            });
            existing.evictOnlyWhenActive =
              existing.evictOnlyWhenActive && group.evictOnlyWhenActive;
            existing.emitDeactivations = existing.emitDeactivations && group.emitDeactivations;
          } else {
            map.set(memberKey, {
              peers: [...excluded],
              evictOnlyWhenActive: group.evictOnlyWhenActive,
              emitDeactivations: group.emitDeactivations,
            });
          }
        });
      });
    });

  return map;
}

/**
 * Build the default exclusion groups applied when no adapter config is present.
 *
 * ## Why this layer keeps defaults when VTS does not
 *
 * VTS deliberately ships almost none: it keys undeclared one-hot messages to
 * their own slot and treats an exclusion declaration as *evidence* that two
 * messages are mutually exclusive, never inferring it. It has to. VTS is the
 * source of truth — anything it invents is persisted to QuestDB and fanned out
 * as though a vendor had reported it, and it previously collapsed every
 * `Usage` leaf into one slot, so `Up` asserting emitted `FrontDoorClosed=false`
 * with no pin behind it.
 *
 * This map is a *presentation* rule over a delta stream that is already
 * authoritative. It writes nothing back, persists nothing, and is scoped to
 * sets that are genuinely one-of-N for a rendered elevator: travel direction,
 * front door state, rear door state. Its purpose is to keep the grid from
 * lighting two contradictory icons during the window between a vendor
 * asserting a new state and de-asserting the old one — on the board that
 * produced the original report, that window was ~4 seconds.
 *
 * The groups are therefore intentionally narrower than a category: no rule
 * spans direction and door state, which is why this layer never reproduced the
 * VTS defect. `exclusionEviction.test.ts` asserts that separation directly.
 *
 * An adapter that supplies a `OneHotExclusionConfig` replaces this map
 * wholesale via {@link buildExclusionMap}; the wizard's exclusion editor writes
 * that config.
 */
export function buildDefaultExclusionGroups(
  usageName: (v: number) => string,
  Usage: {
    Up: number;
    Down: number;
    Parked: number;
    FrontDoorOpening: number;
    FrontDoorClosing: number;
    FrontDoorOpened: number;
    FrontDoorClosed: number;
    FrontDoorStalled: number;
    RearDoorOpening: number;
    RearDoorClosing: number;
    RearDoorOpened: number;
    RearDoorClosed: number;
    RearDoorStalled: number;
  },
): Map<ExclusionKey, ExclusionRule> {
  const map = new Map<ExclusionKey, ExclusionRule>();

  const groups: number[][] = [
    [Usage.Up, Usage.Down, Usage.Parked],
    [
      Usage.FrontDoorOpening,
      Usage.FrontDoorClosing,
      Usage.FrontDoorOpened,
      Usage.FrontDoorClosed,
      Usage.FrontDoorStalled,
    ],
    [
      Usage.RearDoorOpening,
      Usage.RearDoorClosing,
      Usage.RearDoorOpened,
      Usage.RearDoorClosed,
      Usage.RearDoorStalled,
    ],
  ];

  groups.forEach((members) => {
    members.forEach((member, i) => {
      const key = `Usage:${usageName(member)}`;
      const excluded = members.filter((_, j) => j !== i).map((v) => `Usage:${usageName(v)}`);
      // Inline the schema defaults here rather than referencing
      // DEFAULT_LEGACY_EXCLUSION_RULE — buildDefaultExclusionGroups runs at
      // applyDeltaToState module-load time (which imports from this file),
      // and the constant initializer has not yet executed at that point.
      map.set(key, {
        peers: excluded,
        evictOnlyWhenActive: true,
        emitDeactivations: false,
      });
    });
  });

  return map;
}
